import React, { useMemo, useRef } from 'react'
import type { PlotViewport } from '../contract'
import { usePlotGraphicBudget } from '../_shared/usePlotGraphicBudget'

import { PlotDataOf, PlotLabelsOf, PlotPropsOf, StoredPlotPayload } from '../contract'
import { PriceCurvesSlideView } from './PriceCurvesSlideView'
import {
  PRICE_SERIES_ORDER,
  PRICE_SERIES_STYLE,
  PRICE_SHARE_TICKS,
  PriceCurvesRole,
  fillTemplate,
  formatPlotPrice,
  priceTicks,
} from './style'

/**
 * PriceCurvesPlot: the `price-curves` kind at card size (summary card, survey
 * tab, quick-study). Plots plan G.
 *
 * Drawn from the canonical `PriceCurve` only (Python's `build_price_curve`), in
 * the registry payload shape. It is aligned to the deck slide
 * (`PriceSensitivitySlide`), which keeps its own layout: the same ramp and
 * dashes per role (`style.ts`), a 0 to 100% share axis, 3 to 5 price ticks, the
 * acceptable band with its two edges, the optimal line, the legend below.
 *
 * GEOMETRY IS EXACT, LABELS ARE ROUNDED. Marks sit on the exact crossings
 * (`pointOfMarginalCheapness`, `optimalPricePoint`, ...); every printed price
 * reads `reported`, formatted with the payload's `decimals`. When the producer
 * withheld the optimum (`optimalPricePoint` null or `reported.point` null) no
 * marker and no number is drawn, and the withheld notice says so.
 *
 * Every word is a label key from `deck_labels.py` (or the app's translation of
 * the same key). A label the payload does not carry is not drawn: the plot
 * never falls back to English.
 */

export type PriceCurvesPlotData = Omit<PlotDataOf<'price-curves'>, 'labels'> & {
  labels: PriceCurvesPlotLabels
}

/**
 * The kind's label keys, all optional on a read. `price_sensitivity_method_note`
 * is the deck's method caveat, drawn under the legend when a caller supplies it.
 */
export type PriceCurvesPlotLabels = Partial<PlotLabelsOf<'price-curves'>> & {
  price_sensitivity_method_note?: string
}

export interface PriceCurvesPlotProps {
  viewport?: PlotViewport
  data: PriceCurvesPlotData | StoredPlotPayload<'price-curves'>['data']
  props?: PlotPropsOf<'price-curves'>
  /** The base sentence the surface prints under the plot (who answered). */
  baseLine?: string | null
  testIdPrefix?: string
  /** Slide size only: the slide's axis title (its declared unit label). */
  xAxisTitle?: string | null
}

/** Card geometry, in SVG user units (the SVG scales to its container width). */
const VIEW = { width: 520, height: 240 }
const PLOT = { left: 44, right: 504, top: 26, bottom: 176 }
const MARKER_TOP = 18
const TOP_LABEL_BASELINE = 11
const X_TICK_BASELINE = 192
const EDGE_CAPTION_TOP = 200
/**
 * The top labels are HTML (`text-xs` semibold), so their pixel width does not
 * follow the viewBox. 6.4px is that face's advance; the gap is the same 8px
 * clearance at whatever width the card actually gives the graphic.
 */
const CSS_CHAR_PX = 6.4
const TOP_LABEL_GAP_PX = 8
/** Half glyph advances for the band-edge label (10px) and value (13px semibold). */
const EDGE_LABEL_HALF_CHAR = 2.8
const EDGE_VALUE_HALF_CHAR = 4
const EDGE_LABEL_GAP = 10

const MUTED = 'var(--slide-text-muted, #737373)'
// The surface's own text colour (theme-aware on the app's dark card).
const INK = 'currentColor'
const EDGE = 'var(--slide-text-secondary, #384242)'
const GRID = 'var(--slide-border-soft, #E2E2E2)'
const BAND_FILL = 'var(--slide-neutral-mid, #D7D7D7)'
const REFERENCE = 'var(--slide-diverging-negative, #C2311F)'
const BAND_OPACITY = 0.22

interface TopLabel {
  key: 'optimum' | 'reference'
  x: number
  text: string
  anchor: 'middle' | 'start' | 'end'
}

function priceToX(price: number, window: { low: number; high: number }): number {
  const span = window.high - window.low
  if (!Number.isFinite(span) || span <= 0) {
    return PLOT.left
  }
  return PLOT.left + ((price - window.low) / span) * (PLOT.right - PLOT.left)
}

function shareToY(share: number): number {
  const clamped = Math.max(0, Math.min(100, share))
  return PLOT.bottom - (clamped / 100) * (PLOT.bottom - PLOT.top)
}

function viewBoxPerPx(renderedWidth: number): number {
  const width = renderedWidth > 0 ? renderedWidth : VIEW.width
  return VIEW.width / width
}

function labelHalf(text: string, renderedWidth: number): number {
  return (text.length * CSS_CHAR_PX) / 2 * viewBoxPerPx(renderedWidth)
}

/**
 * Keeps centred top labels inside the frame and off each other. Marks never
 * move. A label clamped to the right edge shifts its neighbour left, so the
 * pair keeps its pixel gap when the card is narrower than the design width.
 */
export function placePriceCurveTopLabels(labels: Array<TopLabel>, renderedWidth: number): Array<TopLabel> {
  const gap = TOP_LABEL_GAP_PX * viewBoxPerPx(renderedWidth)
  const placed = labels.map((label) => {
    if (label.anchor !== 'middle') {
      return label
    }
    const half = labelHalf(label.text, renderedWidth)
    return { ...label, x: Math.max(PLOT.left + half, Math.min(PLOT.right - half, label.x)) }
  })
  if (placed.length !== 2 || placed.some((label) => label.anchor !== 'middle')) {
    return placed
  }
  const halves = placed.map((label) => labelHalf(label.text, renderedWidth))
  const needed = halves[0] + halves[1] + gap
  const leftIndex = placed[0].x <= placed[1].x ? 0 : 1
  const rightIndex = leftIndex === 0 ? 1 : 0
  let leftX = placed[leftIndex].x
  let rightX = placed[rightIndex].x
  if (rightX - leftX < needed) {
    rightX = Math.min(rightX, PLOT.right - halves[rightIndex])
    leftX = rightX - needed
    if (leftX < PLOT.left + halves[leftIndex]) {
      leftX = PLOT.left + halves[leftIndex]
      rightX = Math.min(PLOT.right - halves[rightIndex], leftX + needed)
    }
  }
  const xs = [0, 0]
  xs[leftIndex] = leftX
  xs[rightIndex] = rightX
  return placed.map((label, index) => ({ ...label, x: xs[index] }))
}

/**
 * Two labels centred on marks, kept apart by `gap` and inside the frame. Only
 * the labels move; the marks stay on their exact prices.
 */
function spreadPair(x1: number, half1: number, x2: number, half2: number, gap: number): [number, number] {
  const clamp = (x: number, half: number) => Math.max(PLOT.left + half, Math.min(PLOT.right - half, x))
  let a = clamp(x1, half1)
  let b = clamp(x2, half2)
  const needed = half1 + half2 + gap
  if (b - a < needed) {
    const centre = (a + b) / 2
    a = clamp(centre - needed / 2, half1)
    b = clamp(a + needed, half2)
    a = Math.min(a, b - needed)
  }
  return [a, b]
}

function seriesTestId(prefix: string, role: PriceCurvesRole): string {
  return `${prefix}-series-${role.replace(/_/g, '-')}`
}

export function PriceCurvesPlot(props: PriceCurvesPlotProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const graphicRef = useRef<HTMLDivElement>(null)
  const graphics = useMemo(() => [graphicRef], [])
  const budget = usePlotGraphicBudget(props.viewport, rootRef, graphics)
  const { data, baseLine, testIdPrefix = 'price-curves-plot' } = props
  const renderProps = props.props ?? {}

  // Slide size: the deck's joint Van Westendorp layout, frozen (plots wave 7, D2).
  if (renderProps.size === 'slide') {
    return <PriceCurvesSlideView data={data} xAxisTitle={props.xAxisTitle} testIdPrefix={testIdPrefix} />
  }

  const labels: PriceCurvesPlotLabels = data.labels ?? {}
  const locale = renderProps.locale
  const decimals = data.decimals ?? 0
  const currency = data.currencyCode ?? null
  const reported = data.reported
  const showReference = renderProps.showReference !== false
  const showWithheldNotice = renderProps.showWithheldNotice !== false
  const showMethodNote = renderProps.showMethodNote !== false

  const series = PRICE_SERIES_ORDER.map((role) => data.series.find((entry) => entry.role === role)).filter(
    (entry): entry is PriceCurvesPlotData['series'][number] => Boolean(entry),
  )
  const drawnPrices = series.flatMap((entry) => entry.points.map((point) => point.price))
  const window =
    renderProps.axis === 'bounds' && drawnPrices.length > 0
      ? { low: Math.min(...drawnPrices), high: Math.max(...drawnPrices) }
      : data.displayWindow

  const price = (value: number, digits = decimals) => formatPlotPrice(value, currency, digits, locale)
  const publishesPoint = typeof reported.point === 'number' && typeof data.optimalPricePoint === 'number'
  const separator = labels.price_sensitivity_range_separator ?? ''
  const rangeText = [price(reported.low), separator, price(reported.high)].filter(Boolean).join(' ')

  const bandX1 = priceToX(data.pointOfMarginalCheapness, window)
  const bandX2 = priceToX(data.pointOfMarginalExpensiveness, window)
  const lowText = price(reported.low)
  const highText = price(reported.high)
  const edgeHalf = (label: string | undefined, value: string) =>
    Math.max((label ?? '').length * EDGE_LABEL_HALF_CHAR, value.length * EDGE_VALUE_HALF_CHAR)
  const [edgeLowX, edgeHighX] = spreadPair(
    bandX1,
    edgeHalf(labels.price_sensitivity_lower_endpoint, lowText),
    bandX2,
    edgeHalf(labels.price_sensitivity_upper_endpoint, highText),
    EDGE_LABEL_GAP,
  )
  const optimumX = publishesPoint ? priceToX(data.optimalPricePoint as number, window) : null

  // The reference prints at its own configured precision when stamped, else
  // at the corridor's (the payload's one `decimals`).
  const reference = showReference && currency ? data.reference ?? null : null
  const referenceDigits =
    reference && typeof reference.displayDecimals === 'number' ? reference.displayDecimals : decimals
  const referenceOnScale =
    reference != null && reference.value >= window.low && reference.value <= window.high
  const referenceX = reference && referenceOnScale ? priceToX(reference.value, window) : null

  const topLabels: Array<TopLabel> = []
  let offScaleNote: string | null = null
  if (optimumX != null && labels.price_sensitivity_optimum_label) {
    topLabels.push({
      key: 'optimum',
      x: optimumX,
      text: `${labels.price_sensitivity_optimum_label} ${price(reported.point as number)}`,
      anchor: 'middle',
    })
  }
  if (reference && labels.price_sensitivity_reference_label) {
    const value = price(reference.value, referenceDigits)
    if (referenceOnScale) {
      topLabels.push({
        key: 'reference',
        x: referenceX as number,
        text: `${labels.price_sensitivity_reference_label} ${value}`,
        anchor: 'middle',
      })
    } else if (labels.plot_off_scale_note) {
      // The window is the producer's and is never widened for a spend that
      // sits past it. The note sits with the range, not on the plot's top
      // edge, so it cannot cover the optimum label.
      const beyondHigh = reference.value > window.high
      const note = fillTemplate(labels.plot_off_scale_note, {
        label: labels.price_sensitivity_reference_label,
        value,
      })
      offScaleNote = beyondHigh ? `${note} →` : `← ${note}`
    }
  }
  const graphicWidth = budget
    ? Math.max(VIEW.width, Math.min(budget.width, budget.height * VIEW.width / VIEW.height))
    : VIEW.width
  const placedTopLabels = placePriceCurveTopLabels(topLabels, graphicWidth)
  const ticks = priceTicks(window)
  const xAxisTitle = currency ?? labels.price_sensitivity_x_axis_title_fallback ?? ''

  return (
    <div ref={rootRef} data-testid={testIdPrefix} data-viewport-infeasible={budget ? budget.height < VIEW.height || budget.width < VIEW.width : undefined} className="w-full flex flex-col gap-2 text-foreground">
      <div className="flex w-full flex-wrap items-end justify-between gap-x-6 gap-y-1">
        <div data-testid={`${testIdPrefix}-range`}>
          {labels.price_sensitivity_range_label ? (
            <span className="block text-xs text-muted-foreground">{labels.price_sensitivity_range_label}</span>
          ) : null}
          <span
            data-testid={`${testIdPrefix}-range-value`}
            className="block text-2xl font-semibold leading-tight text-foreground tabular-nums"
          >
            {rangeText}
          </span>
        </div>
        {!publishesPoint && showWithheldNotice && labels.price_sensitivity_optimum_label && labels.price_sensitivity_point_withheld ? (
          <PriceCurvesNotice
            testId={`${testIdPrefix}-point-withheld`}
            text={`*${labels.price_sensitivity_optimum_label}: ${labels.price_sensitivity_point_withheld}`}
          />
        ) : null}
        {offScaleNote ? (
          <span
            data-testid={`${testIdPrefix}-reference-off-scale`}
            className="max-w-full text-xs font-semibold leading-snug"
            style={{ color: REFERENCE }}
          >
            {offScaleNote}
          </span>
        ) : null}
      </div>
      {labels.price_sensitivity_y_axis_title ? (
        <span data-testid={`${testIdPrefix}-y-axis-title`} className="text-xs text-muted-foreground">
          {labels.price_sensitivity_y_axis_title}
        </span>
      ) : null}
      <div ref={graphicRef} className="relative" style={budget ? { width: graphicWidth, alignSelf: 'center' } : { width: '100%' }}>
      <svg className="block w-full"
        data-testid={`${testIdPrefix}-chart`}
        viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
        width="100%"
        role="img"
        aria-label={labels.price_sensitivity_range_label ? `${labels.price_sensitivity_range_label} ${rangeText}` : rangeText}
      >
        {PRICE_SHARE_TICKS.map((share) => (
          <React.Fragment key={share}>
            <line x1={PLOT.left} x2={PLOT.right} y1={shareToY(share)} y2={shareToY(share)} stroke={GRID} strokeWidth={1} />
            <text
              data-testid={`${testIdPrefix}-share-tick-${share}`}
              x={PLOT.left - 6}
              y={shareToY(share) + 3.5}
              textAnchor="end"
              fontSize={10}
              fill={MUTED}
            >
              {`${share}%`}
            </text>
          </React.Fragment>
        ))}

        <rect
          data-testid={`${testIdPrefix}-band`}
          x={bandX1}
          y={PLOT.top}
          width={Math.max(0, bandX2 - bandX1)}
          height={PLOT.bottom - PLOT.top}
          fill={BAND_FILL}
          fillOpacity={BAND_OPACITY}
        />
        <line data-testid={`${testIdPrefix}-band-edge-low`} x1={bandX1} x2={bandX1} y1={PLOT.top} y2={PLOT.bottom} stroke={EDGE} strokeWidth={1.2} />
        <line data-testid={`${testIdPrefix}-band-edge-high`} x1={bandX2} x2={bandX2} y1={PLOT.top} y2={PLOT.bottom} stroke={EDGE} strokeWidth={1.2} />

        {series.map((entry) => {
          const style = PRICE_SERIES_STYLE[entry.role]
          return (
            <polyline
              key={entry.role}
              data-testid={seriesTestId(testIdPrefix, entry.role)}
              fill="none"
              stroke={style.stroke}
              strokeWidth={style.width * 0.8}
              strokeDasharray={style.dash}
              points={entry.points
                .map((point) => `${priceToX(point.price, window).toFixed(2)},${shareToY(point.share).toFixed(2)}`)
                .join(' ')}
            />
          )
        })}

        {optimumX != null ? (
          <>
            <line
              data-testid={`${testIdPrefix}-optimum-marker`}
              x1={optimumX}
              x2={optimumX}
              y1={MARKER_TOP}
              y2={PLOT.bottom}
              stroke={INK}
              strokeWidth={1.2}
              strokeDasharray="3 3"
            />
            <circle cx={optimumX} cy={PLOT.bottom} r={3} fill={INK} />
          </>
        ) : null}
        {referenceX != null ? (
          <line
            data-testid={`${testIdPrefix}-reference-marker`}
            x1={referenceX}
            x2={referenceX}
            y1={MARKER_TOP}
            y2={PLOT.bottom}
            stroke={REFERENCE}
            strokeWidth={1.5}
            strokeDasharray="2 4"
          />
        ) : null}
        <line x1={PLOT.left} x2={PLOT.right} y1={PLOT.bottom} y2={PLOT.bottom} stroke={BAND_FILL} strokeWidth={1} />
        {ticks.map((tick) => (
          <React.Fragment key={tick}>
            <line x1={priceToX(tick, window)} x2={priceToX(tick, window)} y1={PLOT.bottom} y2={PLOT.bottom + 4} stroke={BAND_FILL} strokeWidth={1} />
            <text
              data-testid={`${testIdPrefix}-price-tick`}
              x={priceToX(tick, window)}
              y={X_TICK_BASELINE}
              textAnchor="middle"
              fontSize={10}
              fill={MUTED}
            >
              {price(tick, Number.isInteger(tick) ? 0 : decimals)}
            </text>
          </React.Fragment>
        ))}

      </svg>
      {placedTopLabels.map((label) => (
        <span
          key={label.key}
          data-testid={
            label.key === 'optimum'
              ? `${testIdPrefix}-optimum-label`
              : `${testIdPrefix}-reference-label`
          }
          className="pointer-events-none absolute whitespace-nowrap text-xs font-semibold leading-none"
          style={{
            left: `${(label.x / VIEW.width) * 100}%`,
            top: `${(TOP_LABEL_BASELINE / VIEW.height) * 100}%`,
            transform: 'translate(-50%, -100%)',
            color: label.key === 'optimum' ? INK : REFERENCE,
          }}
        >
          {label.text}
        </span>
      ))}
      <span
        className="pointer-events-none absolute flex flex-col items-center"
        style={{
          left: `${(edgeLowX / VIEW.width) * 100}%`,
          top: `${(EDGE_CAPTION_TOP / VIEW.height) * 100}%`,
          transform: 'translateX(-50%)',
        }}
      >
        {labels.price_sensitivity_lower_endpoint ? (
          <span className="text-[11px] leading-tight text-muted-foreground">{labels.price_sensitivity_lower_endpoint}</span>
        ) : null}
        <span data-testid={`${testIdPrefix}-edge-low-value`} className="text-xs font-semibold leading-tight tabular-nums">
          {lowText}
        </span>
      </span>
      <span
        className="pointer-events-none absolute flex flex-col items-center"
        style={{
          left: `${(edgeHighX / VIEW.width) * 100}%`,
          top: `${(EDGE_CAPTION_TOP / VIEW.height) * 100}%`,
          transform: 'translateX(-50%)',
        }}
      >
        {labels.price_sensitivity_upper_endpoint ? (
          <span className="text-[11px] leading-tight text-muted-foreground">{labels.price_sensitivity_upper_endpoint}</span>
        ) : null}
        <span data-testid={`${testIdPrefix}-edge-high-value`} className="text-xs font-semibold leading-tight tabular-nums">
          {highText}
        </span>
      </span>
      </div>
      {xAxisTitle ? (
        <span data-testid={`${testIdPrefix}-x-axis-title`} className="self-end text-xs text-muted-foreground">
          {xAxisTitle}
        </span>
      ) : null}

      <div data-testid={`${testIdPrefix}-legend`} className="flex flex-wrap gap-x-4 gap-y-1">
        {series.map((entry) => {
          const style = PRICE_SERIES_STYLE[entry.role]
          const label = labels[`price_role_${entry.role}` as const] ?? entry.label
          return (
            <span
              key={entry.role}
              data-testid={`${testIdPrefix}-legend-item-${entry.role.replace(/_/g, '-')}`}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
            >
              <svg width={26} height={8} aria-hidden="true" className="shrink-0">
                <line x1={0} y1={4} x2={26} y2={4} stroke={style.stroke} strokeWidth={style.width * 0.8} strokeDasharray={style.dash} />
              </svg>
              {label}
            </span>
          )
        })}
      </div>
      {currency == null && labels.plot_price_no_currency ? (
        <span data-testid={`${testIdPrefix}-no-currency`} className="text-xs text-muted-foreground leading-snug">
          {labels.plot_price_no_currency}
        </span>
      ) : null}
      {baseLine || (showMethodNote && labels.price_sensitivity_method_note) ? (
        <p data-testid={`${testIdPrefix}-footer`} className="text-xs text-muted-foreground leading-snug">
          {baseLine ? <span data-testid={`${testIdPrefix}-base-line`}>{baseLine}</span> : null}
          {baseLine && showMethodNote && labels.price_sensitivity_method_note ? ' ' : null}
          {showMethodNote && labels.price_sensitivity_method_note ? (
            <span data-testid={`${testIdPrefix}-method-note`}>{labels.price_sensitivity_method_note}</span>
          ) : null}
        </p>
      ) : null}
    </div>
  )
}

export interface PriceCurvesNoticeProps {
  text: string
  testId: string
}

/** The plot's one-line notice: why a figure (or the whole curve) is not drawn. */
export function PriceCurvesNotice(props: PriceCurvesNoticeProps) {
  const { text, testId } = props

  return (
    <span data-testid={testId} className="text-xs text-muted-foreground">
      {text}
    </span>
  )
}

export interface PriceCurvesRefusalProps {
  /** The producer's refusal copy, already in the reader's language. */
  message: string
  testIdPrefix?: string
}

/**
 * A refused curve (no crossing, too few answers...): the plot's notice alone,
 * stating the producer's typed reason. No axes and no curves are drawn, because
 * the canonical producer publishes none for a refused curve.
 */
export function PriceCurvesRefusal(props: PriceCurvesRefusalProps) {
  const { message, testIdPrefix = 'price-curves-plot' } = props

  return (
    <div data-testid={`${testIdPrefix}-refused`} className="w-full flex flex-col gap-2 text-foreground">
      <PriceCurvesNotice testId={`${testIdPrefix}-refusal-reason`} text={message} />
    </div>
  )
}
